import { useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MODE_OPTIONS, ensureSoftCosts, softCostItemTotal, softCostsTotal,
  defaultBondRateForCurrency,
} from "@/lib/softCostsTemplates";
import { estimatedRenovationCost } from "@/lib/calculations";
import { useFormatCurrency } from "@/lib/CurrencyContext";

export default function SoftCostsTab({ project, onChange }) {
  const items = project.soft_costs || [];
  const fmt = useFormatCurrency();
  const currency = project?.currency || "USD";

  useEffect(() => {
    if (items.length === 0) {
      onChange({ soft_costs: ensureSoftCosts([], currency) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const merged = useMemo(() => ensureSoftCosts(items, currency), [items, currency]);
  const renoCost = useMemo(() => estimatedRenovationCost(project), [project]);
  const total = softCostsTotal(merged, project, renoCost);
  const enabledCount = merged.filter((i) => i.enabled).length;

  const updateItem = (id, patch) => {
    const next = merged.map((i) => (i.id === id ? { ...i, ...patch } : i));
    onChange({ soft_costs: next });
  };

  return (
    <div className="space-y-6 fc-fade-in" data-testid="softcosts-tab">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="fc-heading text-xl text-[#1C1C1C]">Soft Costs</h2>
          <p className="text-xs text-[#A8A29E] mt-1 max-w-xl">
            Fees, holding, commission. Each line can be a fixed amount or a percentage of purchase, sale, or renovation cost.
          </p>
        </div>
        <div className="text-right">
          <div className="fc-label">Soft Costs Total</div>
          <div className="fc-heading text-2xl text-[#1C1C1C] mt-0.5" data-testid="softcosts-grand-total">
            {fmt(total)}
          </div>
          <div className="text-[11px] text-[#A8A29E] mt-0.5">
            {enabledCount} of {merged.length} enabled
          </div>
        </div>
      </div>

      {/* Desktop table — hidden on mobile */}
      <div className="fc-card p-0 overflow-hidden hidden md:block">
        <div className="overflow-x-auto fc-no-scrollbar">
          <table className="w-full min-w-[680px] text-sm" data-testid="softcosts-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-sage-700 bg-sage-50 border-b border-sage-200">
              <th className="py-3 px-5 font-normal w-10">On</th>
              <th className="py-3 px-2 font-normal">Item</th>
              <th className="py-3 px-2 font-normal w-44">Mode</th>
              <th className="py-3 px-2 font-normal w-32">Value</th>
              <th className="py-3 px-5 font-normal w-36 text-right">Computed</th>
            </tr>
          </thead>
          <tbody>
            {merged.map((it) => {
              const computed = softCostItemTotal(it, project, renoCost);
              const isAutoBond = it.mode === "bond_interest_auto";
              const isAnnual = it.mode === "annual_prorated";
              const isPerSqm = it.mode === "per_sqm";
              const isPerRoom = it.mode === "per_room";
              const dollarMode = it.mode === "fixed" || isAnnual || isPerSqm || isPerRoom;
              const unitSuffix = dollarMode
                ? (isPerSqm ? "/sqm" : isPerRoom ? "/rm" : "$")
                : "%";
              // For the auto bond-interest mode the line ignores its own
              // `value` — we show the *effective* period rate that's being
              // applied (annual rate × months ÷ 12) so the user sees where
              // the number comes from and how their Key Inputs are
              // driving it.
              const effectiveAutoRate = isAutoBond
                ? (
                    (Number(project?.bond_rate) > 0
                      ? Number(project.bond_rate)
                      : defaultBondRateForCurrency(project?.currency || "USD"))
                    * (Number(project?.timeline_months) > 0
                      ? Number(project.timeline_months)
                      : 4)
                    / 12
                  )
                : null;
              const displayValue = isAutoBond
                ? (effectiveAutoRate || 0).toFixed(2)
                : it.value;
              return (
                <tr
                  key={it.id}
                  className={`border-t border-black/[0.04] transition-opacity ${it.enabled ? "" : "opacity-50"}`}
                  data-testid={`soft-row-${it.key}`}
                >
                  <td className="py-3 px-5">
                    <Switch
                      data-testid={`soft-toggle-${it.key}`}
                      checked={it.enabled}
                      onCheckedChange={(v) => updateItem(it.id, { enabled: !!v })}
                    />
                  </td>
                  <td className="py-3 px-2 text-[#1C1C1C]">{it.label}</td>
                  <td className="py-3 px-2">
                    <Select
                      value={it.mode}
                      onValueChange={(v) => updateItem(it.id, { mode: v })}
                    >
                      <SelectTrigger
                        data-testid={`soft-mode-${it.key}`}
                        className="fc-input h-8 rounded-md text-sm"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
                        {MODE_OPTIONS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 px-2">
                    <div className="relative">
                      <Input
                        data-testid={`soft-value-${it.key}`}
                        type="number"
                        value={displayValue}
                        onChange={(e) => updateItem(it.id, { value: Number(e.target.value) || 0 })}
                        disabled={isAutoBond}
                        title={isAutoBond ? "Auto-calculated from Key Inputs (annual rate × months ÷ 12)" : undefined}
                        className={`fc-input h-8 rounded-md text-sm pr-6 ${isAutoBond ? "opacity-60 cursor-not-allowed" : ""}`}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A8A29E]">
                        {unitSuffix}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-5 text-right fc-mono text-[#1C1C1C]" data-testid={`soft-computed-${it.key}`}>
                    {fmt(computed)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Mobile stacked-card layout — only on < md */}
      <div className="md:hidden space-y-3" data-testid="softcosts-mobile-list">
        {merged.map((it) => {
          const computed = softCostItemTotal(it, project, renoCost);
          const isAutoBond = it.mode === "bond_interest_auto";
          const isAnnual = it.mode === "annual_prorated";
          const isPerSqm = it.mode === "per_sqm";
          const isPerRoom = it.mode === "per_room";
          const dollarMode = it.mode === "fixed" || isAnnual || isPerSqm || isPerRoom;
          const unitSuffix = dollarMode
            ? (isPerSqm ? "/sqm" : isPerRoom ? "/rm" : "$")
            : "%";
          const effectiveAutoRate = isAutoBond
            ? (
                (Number(project?.bond_rate) > 0
                  ? Number(project.bond_rate)
                  : defaultBondRateForCurrency(project?.currency || "USD"))
                * (Number(project?.timeline_months) > 0
                  ? Number(project.timeline_months)
                  : 4)
                / 12
              )
            : null;
          const displayValue = isAutoBond
            ? (effectiveAutoRate || 0).toFixed(2)
            : it.value;
          return (
            <div
              key={it.id}
              data-testid={`soft-card-${it.key}`}
              className={`fc-card p-4 transition-opacity ${it.enabled ? "" : "opacity-60"}`}
            >
              {/* Row 1: switch + label / computed */}
              <div className="flex items-start gap-3">
                <Switch
                  data-testid={`soft-toggle-mobile-${it.key}`}
                  checked={it.enabled}
                  onCheckedChange={(v) => updateItem(it.id, { enabled: !!v })}
                  className="mt-0.5 shrink-0"
                />
                <div className="flex-1 min-w-0 flex items-start justify-between gap-3">
                  <div className="text-sm text-[#1C1C1C] leading-tight pt-0.5">{it.label}</div>
                  <div
                    className="fc-mono text-sm text-[#1C1C1C] whitespace-nowrap pt-0.5"
                    data-testid={`soft-computed-mobile-${it.key}`}
                  >
                    {fmt(computed)}
                  </div>
                </div>
              </div>

              {/* Row 2: Mode + Value */}
              <div className="grid grid-cols-[1fr_110px] gap-2 mt-3">
                <Select
                  value={it.mode}
                  onValueChange={(v) => updateItem(it.id, { mode: v })}
                >
                  <SelectTrigger
                    data-testid={`soft-mode-mobile-${it.key}`}
                    className="fc-input h-9 rounded-md text-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
                    {MODE_OPTIONS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Input
                    data-testid={`soft-value-mobile-${it.key}`}
                    type="number"
                    value={displayValue}
                    onChange={(e) => updateItem(it.id, { value: Number(e.target.value) || 0 })}
                    disabled={isAutoBond}
                    title={isAutoBond ? "Auto from Key Inputs" : undefined}
                    className={`fc-input h-9 rounded-md text-sm pr-6 text-right fc-mono ${isAutoBond ? "opacity-60 cursor-not-allowed" : ""}`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A8A29E]">
                    {unitSuffix}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
